import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, Button, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Employee } from '../../../types/Employee';
import { PassportRequest, PassportRequestStatus, PassportRequestType } from '../../../types/PassportRequest';
import { collection, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, setDoc, where } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';
import employeeConverter from '../../../converters/employeeConverter';
import { v4 as uuidv4 } from 'uuid';

type Props = {
  employee?: Employee;
  passportRequest?: PassportRequest;
  fixedType?: PassportRequestType;
  onSaved?: (passportRequest: PassportRequest) => void;
  onClosed?: () => void;
};
export const PassportRequestForm = (props: Props) => {
  const { passportRequest, fixedType, onSaved, onClosed } = props;
  const editMode = passportRequest !== undefined;

  const { enqueueSnackbar } = useSnackbar();
  const db = getFirestore();

  const [employee, setEmployee] = useState<Employee | undefined>(props.employee);
  const [type, setType] = useState<PassportRequestType>(editMode ? passportRequest.type : 'Renewal');
  const [status, setStatus] = useState<PassportRequestStatus>('New');
  const [reason, setReason] = useState('');
 
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  const [saving, setSaving] = useState(false);

  const searchDebounced = useDebouncedCallback((value: string) => {
    setLoadingEmployees(true);
    setEmployees([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      where('status', '==', 'Active'),
      limit(15),
      orderBy('idNumber'))
    .withConverter(employeeConverter)).then((qSnapshot) => {
      setEmployees(qSnapshot.docs.map((d) => d.data()));
      setLoadingEmployees(false);
    });
  }, 1000);

  const handleOpenAutocomplete = () => setOpenAutocomplete(true);
  const handleCloseAutocomplete = () => setOpenAutocomplete(false);

  const handleTexInputOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const handleAutocompleteSelectChange = (_e: SyntheticEvent<Element, Event>, value: Employee | null) => {
    setEmployee(value ?? undefined);
  };

  const handleCancelOnClick = () => onClosed && onClosed();

  const handleSaveOnClick = () => {
    if (employee) {
      if (window.confirm((editMode ? 'Update ' : 'Create ') + ` employee ${employee.idNumber} passport request?`)) {
        setSaving(true);

        let request: PassportRequest = {
          id: uuidv4(),
          idNumber: employee.idNumber,
          name:  employee.name,
          dateRequested: DateTime.utc().toISO(),
          dateResponded: DateTime.utc().toISO(),
          type,
          reason,
          status: 'Available',
          lastUpdate: DateTime.utc().toISO(),
          uid: employee.uid,
        };

        if (editMode) {
          request = { ...passportRequest, type, reason };
        }

        // Save
        setDoc(doc(db, `Employees/${employee.idNumber}/PassportRequests/${request.id}`), request, { merge: true })
          .then(() => {
            enqueueSnackbar(`Employee ${employee.idNumber} passport request has been ` + (editMode ? 'updated!' : 'created!'), { variant: 'success' });

            setType('Renewal');
            setReason('');
            setSaving(false);

            onSaved && onSaved(request);
          })
          .catch((error) => {
            enqueueSnackbar(typeof(error) === 'string' ? error : error.message, { variant: 'success' });
          });
      }
    }
  };

  useEffect(() => {
    if (editMode) {
      // Get employee information
      const { idNumber } = passportRequest;
      if (props.employee === undefined) {
        getDoc(doc(db, `Employees/${idNumber}`).withConverter(employeeConverter)).then(docSnapshot => {
          setEmployee(docSnapshot.data());
        });
      }
    } else {
      if (props.employee) {
        setEmployee(employee);
      }
    }
  }, [editMode]);

  return (
    <Box>
      <Stack spacing={2.5} padding={3}>
        {employee !== undefined ? (
          <Box mb={2}>
            <Stack direction='row' spacing={2}>
              <Avatar src={employee.picture} />
              <Stack>
                <Typography fontWeight='700'>{employee.name}</Typography>
                <Typography variant='caption'>{employee.idNumber}</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Autocomplete
              id="asynchronous-demo"
              fullWidth
              open={openAutocomplete}
              onOpen={handleOpenAutocomplete}
              onClose={handleCloseAutocomplete}
              isOptionEqualToValue={(option, value) => option.idNumber === value.idNumber}
              getOptionLabel={option => `${option.idNumber} - ${option.name}`}
              options={employees}
              loading={loadingEmployees}
              noOptionsText="Enter employee id number..."
              onChange={handleAutocompleteSelectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Id Number"
                  onChange={handleTexInputOnChanged}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
      {employee !== undefined && (
        <Stack spacing={4}>
          <Box>
            {fixedType !== undefined ? (
              <Stack>
                <Typography variant='caption'>Type</Typography>
                <Box sx={{
                  backgroundColor: theme => theme.palette.mode === 'dark' ? '#0000001f' : '#f2f2f2',
                  padding: 1,
                  borderRadius: 1,
                }}>
                  <Typography>{fixedType}</Typography>
                </Box>
              </Stack>
            ) : (
              <Box />
            )}
          </Box>
          <FormControl fullWidth>
            <InputLabel id="select-status-label">Request Status</InputLabel>
            <Select
              labelId="select-status-label"
              id="select-status"
              label="Request Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as PassportRequestStatus)}
            >
              <MenuItem value={'New' as PassportRequestStatus}>New</MenuItem>
              <MenuItem value={'Processing' as PassportRequestStatus}>Processing</MenuItem>
              <MenuItem value={'Available' as PassportRequestStatus}>Available</MenuItem>
              <MenuItem value={'Completed' as PassportRequestStatus}>Out</MenuItem>
              <MenuItem value={'Returned' as PassportRequestStatus}>Returned</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ p: 1 }} direction='row' justifyContent='right'>
        <Button color='error' onClick={handleCancelOnClick}>CANCEL</Button>
        <Button disabled={saving} onClick={handleSaveOnClick}>SAVE</Button>
      </Stack>
    </Box>
  );
};
